import type {
  ArtifactCreationBody,
  ConfigurationModule,
  FormBuilderHandle,
  FormBuilderIntegrationConfig,
  FormBuilderTemplateLoadError,
  FormBuilderValues,
  ModuleInitParams,
  TemplateDetails,
  TemplateInfoFromList,
} from '@ingenico/react-form-builder';
import { FormBuilderComponent, FormBuilderReadModeEnum, InputKebabOptions } from '@ingenico/react-form-builder';
import { Banner } from '@ingenico/react-ui/banner';
import { CardCustom } from '@ingenico/react-ui/card-custom';
import { CoreButton } from '@ingenico/react-ui/core-button';
import { useSafeLeakSubscriber } from '@ingenico/react-utils/hooks';
import { CircularProgress, LinearProgress } from '@mui/material';
import deepEqual from 'fast-deep-equal';
import { cloneDeep, isEqual, toString } from 'lodash-es';
import type { ReactElement, RefObject } from 'react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { filter, tap } from 'rxjs';
import { useOpenSmartFieldDetailPage } from '../../../hooks/useOpenSmartFieldDetailsPage.hook';
import { CancelModal } from '../../module/cancel-modal/CancelModal.ctrl';
import { ParameterModuleContentActionsContainer } from '../parameter-module-content-actions-container/ParameterModuleContentActionsContainer.ctrl';
import { ParameterModuleContentParametersModal } from '../parameter-module-content-parameters-modal/ParameterModuleContentParametersModal.ctrl';
import { ParameterModuleMapSmartFieldsModal } from '../parameter-module-map-smart-fields-modal/ParameterModuleMapSmartFieldsModal.ctrl';
import type { ParameterModuleTabProps } from './ParameterModuleTab.model';
import styles from './ParameterModuleTab.module.scss';
import { buildModuleInitParams, shouldDisplayModuleNoInputsInfo } from './ParameterModuleTab.utils';
import { useParameterModuleTab } from './ParameterModuleTab.vm';
import { useParameterModuleTemplateResources } from './useParameterModuleTemplateResources.hook';

import { useCurrentUserDateTimeFormat } from '@ingenico/react-section-utils';
import { usePrevious } from '@react-hookz/web';
import { useDeviceManagementCapability } from '../../../hooks';
import { useGetAssetLogo } from '../../../hooks/useGetAssetLogo.hook';

const FORM_BUILDER_GEAR_MENU_OPTIONS = [
  InputKebabOptions.MapSmartField,
  InputKebabOptions.ChangeSmartField,
  InputKebabOptions.ViewSmartField,
  InputKebabOptions.SetEmptyValue,
  InputKebabOptions.Reset,
  InputKebabOptions.UploadFile,
] as const;

const StableFormBuilder = memo(
  ({
    childRef,
    integration,
  }: {
    readonly childRef: RefObject<FormBuilderHandle>;
    readonly integration: FormBuilderIntegrationConfig;
  }) => {
    return <FormBuilderComponent ref={childRef} integration={integration} />;
  },
  (previous, next) => previous.childRef === next.childRef && previous.integration === next.integration,
);

function ParameterModuleTabCtrl({
  actions,
  display: {
    errorBannerMessage,
    shouldDisplayErrorBanner,
    shouldDisplayLinearProgress,
    shouldDisplayFooter,
    shouldDisplayLoadingOverlay,
  },
  editMode,
  messages: { noInputsInfo },
  formBuilderConfig: {
    apiFnList,
    configModule,
    context,
    handleSelectParameter,
    onCancel,
    onError,
    onSave,
    openMapSmartFieldModal,
    parameterSet,
    fetchParameterTemplate,
    templates,
    isRefetchingModule,
  },
  smartField,
  translations,
}: ParameterModuleTabProps): ReactElement {
  const userCanEdit = editMode?.userCanEdit ?? true;
  const hasEnableFormBuilderDynamicChoice = useDeviceManagementCapability(
    'configProfile.enableFormBuilderDynamicChoice',
  );
  const shouldSendDefaultValue = !useDeviceManagementCapability('configProfile.skipDefaultTemplateValues');

  const handleEnterEditMode = editMode?.handleEnterEditMode;

  const childRef = useRef<FormBuilderHandle>(null);
  const pendingTemplateIdRef = useRef<string | null>(null);
  const [hasInitializedRef, setHasInitializedRef] = useState<boolean>(false);
  const subscriptionsInitializedRef = useRef(false);
  const previousContextRef = useRef<string | undefined>(context);
  const shouldRefetchModule = useRef<boolean>(false);
  const [isValidForm, setIsValidForm] = useState<boolean>(true);

  useEffect(() => {
    if (previousContextRef.current !== context && hasInitializedRef && childRef.current) {
      childRef.current.setReadModeTo(
        context === 'readonly' ? FormBuilderReadModeEnum.Read : FormBuilderReadModeEnum.Edit,
      );
    }
    previousContextRef.current = context;
  }, [context, hasInitializedRef]);

  const resetFormBuilderChanges = useCallback((shouldResetAll: boolean, templateId?: string) => {
    if (childRef.current) {
      childRef.current.resetChanges(shouldResetAll, templateId);
    }
  }, []);

  const getNewFormBuilderValues = useCallback((): FormBuilderValues | undefined => {
    if (childRef.current) {
      return childRef.current.getParameterSetValues();
    }
    return undefined;
  }, []);

  const clearChangesAfterSave = useCallback((): void => {
    if (childRef.current) {
      childRef.current.clearChangesAfterSave();
    }
  }, []);

  const triggerFormBuilderValidation = useCallback(() => {
    if (childRef.current) {
      return Promise.resolve(childRef.current.validateModule());
    }
    return undefined;
  }, []);

  const resetFormData = useCallback(() => {
    shouldRefetchModule.current = true;
  }, []);

  const prevParams = useRef<ModuleInitParams | null>(null);
  const moduleTemplateList = useMemo(
    () => (templates?.data?.data as TemplateInfoFromList[] | undefined) ?? [],
    [templates?.data?.data],
  );
  const {
    templateToDisplay: moduleTemplateToDisplay,
    dataFileResourceIds,
    getCurrentDataFileResourceIds,
    setActiveTemplateId,
    loadTemplate,
    onFormBuilderInitialized,
  } = useParameterModuleTemplateResources({
    configModule: configModule as unknown as ConfigurationModule | null | undefined,
    initialTemplate:
      (parameterSet?.data as unknown as TemplateDetails | undefined) ?? prevParams.current?.templateToDisplay,
    getCurrentValues: getNewFormBuilderValues,
    fetchParameterTemplate,
  });

  const {
    handleChangesCount,
    footer,
    previewModal,
    cancelModal: {
      cancelModalTranslations,
      isCancelDialogOpened,
      onHandleCloseCancelDialog,
      onHandleConfirmCancelDialog,
    },
    artifacts: {
      artifactListPayload,
      handleChangeArtifactSearchQuery,
      handleCreateNewArtifact,
      handleCreateSmartFieldArtifact,
      handlePreviewFile,
      isLoading: areArtifactsLoading,
    },
  } = useParameterModuleTab({
    apiFnList,
    clearChangesAfterSave,
    getNewFormBuilderValues,
    onCancel,
    onSave,
    resetFormBuilderChanges,
    triggerFormBuilderValidation,
    resetFormData,
    childRef,
    dataFileResourceIds,
    getCurrentDataFileResourceIds,
  });

  const getNewTemplate = useCallback(
    (value: string) => {
      if (childRef.current) {
        setActiveTemplateId(value);
        handleSelectParameter?.({ label: value, value });
        pendingTemplateIdRef.current = value;
      }
    },
    [handleSelectParameter, setActiveTemplateId],
  );

  const { openSmartFieldDetailPage } = useOpenSmartFieldDetailPage();

  const formBuilderIntegration = useMemo<FormBuilderIntegrationConfig>(
    () => ({
      fetchParameterTemplate:
        fetchParameterTemplate === undefined
          ? undefined
          : ({ templateId }) => {
              pendingTemplateIdRef.current = null;
              return loadTemplate(templateId);
            },
      gearMenu: {
        enabledOptions: FORM_BUILDER_GEAR_MENU_OPTIONS,
      },
      smartFieldMapping: {
        enabled: openMapSmartFieldModal !== undefined,
        requestMapping: ({ fieldId, inputComponent, mode, resolve }) => {
          openMapSmartFieldModal?.(fieldId, mode, inputComponent, (selectedSmartField) => {
            if (selectedSmartField) {
              resolve({
                id: toString(selectedSmartField.id),
                name: selectedSmartField.name,
              });
            }
          });
        },
        viewSmartField: openSmartFieldDetailPage,
      },
    }),
    [fetchParameterTemplate, loadTemplate, openMapSmartFieldModal, openSmartFieldDetailPage],
  );

  useEffect(() => {
    if (
      pendingTemplateIdRef.current &&
      parameterSet?.isSuccess &&
      parameterSet?.data?.id === pendingTemplateIdRef.current
    ) {
      const newSet = parameterSet.data as unknown as TemplateDetails;
      childRef.current?.addParameterSet(newSet);
      pendingTemplateIdRef.current = null;
    }
  }, [childRef, parameterSet?.data, parameterSet?.isSuccess]);

  const subscriber = useSafeLeakSubscriber();

  useEffect(() => {
    if (!childRef.current || !hasInitializedRef) {
      return;
    }
    if (subscriptionsInitializedRef.current) {
      return;
    }
    if (childRef.current && hasInitializedRef) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      subscriber(childRef.current.templateIDToLoad$.pipe(tap((value: any) => getNewTemplate(value))));
      subscriber(childRef.current.currentTemplateId$.pipe(tap(setActiveTemplateId)));
      subscriber(
        childRef.current.templateLoadError$.pipe(
          tap(({ error }: FormBuilderTemplateLoadError) => {
            onError?.(error instanceof Error ? error : new Error('Unable to load parameter template'));
          }),
        ),
      );

      subscriber(
        childRef.current.getChanges$.pipe(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          tap((changes: any) => {
            handleChangesCount(changes);
          }),
        ),
      );
      subscriber(
        childRef.current.formModeChanged$.pipe(
          filter((value) => value === FormBuilderReadModeEnum.Edit),
          tap((_value) => {
            handleEnterEditMode?.();
          }),
        ),
      );
      subscriber(
        childRef.current.isValid$.pipe(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          tap((isValid: any) => {
            setIsValidForm(isValid);
          }),
        ),
      );
      subscriber(
        childRef.current.artifactSearchRequest$.pipe(
          tap(({ query, acceptedExtensions }) => {
            handleChangeArtifactSearchQuery(query, acceptedExtensions);
          }),
        ),
      );
      subscriber(
        childRef.current.uploadFilePayload$.pipe(
          tap((body: ArtifactCreationBody) => {
            handleCreateNewArtifact(body);
          }),
        ),
      );
      subscriber(
        childRef.current.previewFileId$.pipe(
          tap((previewId: string) => {
            handlePreviewFile(previewId);
          }),
        ),
      );
      subscriptionsInitializedRef.current = true;
    }
  }, [
    hasInitializedRef,
    subscriber,
    setActiveTemplateId,
    getNewTemplate,
    openSmartFieldDetailPage,
    openMapSmartFieldModal,
    handleChangesCount,
    handleEnterEditMode,
    handleChangeArtifactSearchQuery,
    handleCreateNewArtifact,
    handlePreviewFile,
    onError,
  ]);

  const userDateTimeFormat = useCurrentUserDateTimeFormat();

  const setUserDateFormatToForm = useCallback(
    () => childRef.current?.setUserDateTimeFormat(userDateTimeFormat),
    [userDateTimeFormat],
  );

  const prevConfigModule = usePrevious(configModule);

  const configModuleHasChanged = useMemo(() => {
    return !isEqual(configModule, prevConfigModule);
  }, [configModule, prevConfigModule]);

  const isModuleContext = context === 'module' || context === 'readonly';

  const hasRefetchedModuleOrReadOnly = useMemo(() => {
    return configModuleHasChanged && isModuleContext && hasInitializedRef;
  }, [configModuleHasChanged, isModuleContext, hasInitializedRef]);

  useEffect(() => {
    const shouldResetModuleFormAfterRefetch =
      hasRefetchedModuleOrReadOnly &&
      (context === 'module' || context === 'readonly') &&
      childRef.current &&
      configModule;
    const params = buildModuleInitParams({
      configModule: configModule as unknown as ConfigurationModule | null | undefined,
      previousParams: prevParams.current as ModuleInitParams | null,
      templateList: moduleTemplateList,
      templateToDisplay: moduleTemplateToDisplay,
      artifactListPayload,
    });
    if (
      (childRef.current &&
        !hasInitializedRef &&
        templates?.isSuccess &&
        parameterSet?.isSuccess &&
        configModule &&
        isModuleContext &&
        params) ||
      (shouldResetModuleFormAfterRefetch && params)
    ) {
      childRef.current.init(
        context,
        params,
        {
          isReadOnly: context === 'readonly',
          componentToggler: { editButton: Boolean(userCanEdit && handleEnterEditMode) },
          shouldSendDefaultValue,
        },
        shouldRefetchModule.current,
        { hasEnableFormBuilderDynamicChoice },
      );
      onFormBuilderInitialized();
      prevParams.current = cloneDeep(params);
      if (shouldRefetchModule.current) {
        shouldRefetchModule.current = false;
      }
      setUserDateFormatToForm();
      setHasInitializedRef(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    templates?.isSuccess,
    parameterSet?.isSuccess,
    configModule,
    moduleTemplateList,
    moduleTemplateToDisplay,
    parameterSet?.data,
    context,
    isModuleContext,
    hasInitializedRef,
    setUserDateFormatToForm,
    configModuleHasChanged,
    hasEnableFormBuilderDynamicChoice,
    shouldSendDefaultValue,
  ]);
  useGetAssetLogo({ childRef, hasInitializedRef, logoId: configModule?.softwareVersion?.softwareLogo?.id });

  useEffect(() => {
    if (isRefetchingModule) {
      // When a refetch is triggered externally (e.g. from the Import parameters modal),
      // we need to ensure the form-builder fully re-initialises from the new data
      // instead of reusing its internal cached state. This mirrors what `resetFormData()`
      // and `clearChangesAfterSave()` do in the Save flow.
      shouldRefetchModule.current = true;
      childRef.current?.clearChangesAfterSave();
    }
    childRef.current?.setShouldDisableEditButton(!!isRefetchingModule);
  }, [isRefetchingModule]);

  useEffect(() => {
    if (hasInitializedRef) {
      childRef.current?.updateArtifactsList(artifactListPayload);
    }
  }, [artifactListPayload, hasInitializedRef]);

  if (shouldDisplayErrorBanner) {
    return <Banner message={errorBannerMessage} />;
  }

  const shouldDisplayNoInputsInfo = shouldDisplayModuleNoInputsInfo({
    configModule: configModule as unknown as ConfigurationModule | null | undefined,
    context,
    isRefetchingModule,
    parameterSetIsSuccess: parameterSet?.isSuccess,
    shouldDisplayLoadingOverlay,
    templateList: moduleTemplateList,
    templateToDisplay: moduleTemplateToDisplay,
    templatesIsSuccess: templates?.isSuccess,
  });

  return shouldDisplayLinearProgress ? (
    <LinearProgress />
  ) : (
    <>
      <div className={styles.container}>
        <CardCustom className={styles.card}>
          <div className={styles.contentContainer}>
            {(shouldDisplayLoadingOverlay || isRefetchingModule) && (
              <div className={styles.loading}>
                <div className={styles.overlay}></div>
                <CircularProgress variant={'indeterminate'} />
              </div>
            )}

            {shouldDisplayNoInputsInfo ? (
              <Banner message={noInputsInfo} />
            ) : (
              <StableFormBuilder childRef={childRef} integration={formBuilderIntegration} />
            )}
          </div>
          {shouldDisplayFooter && !shouldDisplayNoInputsInfo && (
            <ParameterModuleContentActionsContainer
              onSave={footer.handleOpenSaveModal}
              onCancel={footer.handleCancel}
              onClickViewChanges={footer.handleClickViewChanges}
              viewChangesCount={footer.changesCount}
              isLoading={footer.isLoadingValidations}
              isValidForm={isValidForm}
            />
          )}
        </CardCustom>
      </div>
      <ParameterModuleContentParametersModal
        isOpen={previewModal.isOpen}
        onClose={previewModal.handleClose}
        mode={previewModal.mode}
        onConfirm={previewModal.handleConfirm}
        onCancelParameter={previewModal.handleCancelParameter}
        sections={previewModal.sections}
        onError={actions.onError}
      />
      <ParameterModuleMapSmartFieldsModal
        isOpen={smartField.isOpen}
        hasError={smartField.hasError}
        isLoading={smartField.isLoading}
        mode={smartField.mode}
        onClose={smartField.handleClose}
        onConfirm={smartField.handleConfirm}
        onConfirmCreation={smartField.handleConfirmCreation}
        options={smartField.options}
        selectedSmartField={smartField.selectedSmartField}
        categoryOptions={smartField.categoryOptions}
        handleChangeCategories={smartField.handleChangeCategories}
        isLoadingCategories={smartField.isLoadingCategories}
        selectedCategories={smartField.selectedCategories}
        handleCategorySearchChange={smartField.handleCategorySearchChange}
        handleSmartfieldSearchChange={smartField.handleSmartfieldSearchChange}
        parameter={smartField.parameter}
        translations={translations}
        inputKey={smartField.inputKey}
        artifacts={artifactListPayload}
        artifactsIsLoading={areArtifactsLoading}
        onArtifactSearch={handleChangeArtifactSearchQuery}
        onArtifactCreate={handleCreateSmartFieldArtifact}
        onArtifactPreview={handlePreviewFile}
      />
      <CancelModal
        isOpen={isCancelDialogOpened}
        handleOnClose={onHandleCloseCancelDialog}
        translations={cancelModalTranslations}
        actions={
          <>
            <CoreButton onClick={onHandleCloseCancelDialog} variant="text">
              {cancelModalTranslations.buttons.cancel}
            </CoreButton>
            <CoreButton onClick={onHandleConfirmCancelDialog} variant="contained">
              {cancelModalTranslations.buttons.quit}
            </CoreButton>
          </>
        }
      />
    </>
  );
}

export const ParameterModuleTab = memo(ParameterModuleTabCtrl, deepEqual);
